import * as React from 'react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import MediaQuery, { useMediaQuery } from 'react-responsive';

import Autocomplete, {
	AutocompleteProps,
	DropdownRendererParameters
} from '../../Autocomplete/components/Autocomplete';
import { Group as GroupType, Option } from '../../Autocomplete/types';
import { Location } from '../types';
import { filterGroupsForDirectionsDialog, parseLocationGroups } from '../utils';
import MobileDropdown from './MobileDropdown/MobileDropdown';
import { getUserLocation, MOBILE_MIN_WIDTH } from '../../utils';
import { LocationsConfig, LocationType } from '../../types';
import OptionComponent from './Option/Option';
import Dropdown from './Dropdown/Dropdown';
import Group from './Group/Group';
import { EngineTheme, ThemeProvider, useTheme } from '../../theme';
import { useConfig } from '../../context';
import {
	LocationAdditionalGroupType,
	LocationGroupType,
	LocationsQuery,
	Position,
	useLocationsQuery
} from '@websky/graphql';
import DirectionsDialog from './DirectionsDialog/DirectionsDialog';

interface OwnProps {
	ref?: React.Ref<HTMLDivElement>;
	type?: LocationType;
	locations?: LocationsConfig;
	recentSearches?: Location[];
	onNearestAirports?: (locations: Location[]) => void;
}

export type Props = Partial<AutocompleteProps<Location>> & OwnProps;

const optionRenderer = (option: Option<Location>, key: number, onClick: (event: React.MouseEvent) => void) => (
	<OptionComponent key={key} option={option} onClick={onClick} />
);

const optionGroupRenderer = (
	group: GroupType<Location>,
	key: number,
	optionRenderer: (option: Option<Location>, key: number) => React.ReactNode
) => <Group key={key} group={group} optionRenderer={optionRenderer} />;

const LocationAutocomplete = forwardRef<HTMLDivElement, Props>((props, ref) => {
	const {
		recentSearches = [],
		locations = {},
		type,
		onNearestAirports,
		onSelect: onSelectProp,
		onBlur: onBlurProp,
		onClickOutsideProps,
		...autocompleteProps
	} = props;

	const { Autocomplete: theme } = useTheme('LocationAutocomplete');
	const [query, setQuery] = useState('');
	const [lastSuggestion, setLastSuggestion] = useState<LocationsQuery>();
	const [delayLoading, setDelayLoading] = useState(false);
	const [dialogIsOpen, setDialogIsOpen] = useState(false);

	const [coordinates, setCoordinates] = useState<Position>(null);
	const {
		SearchForm: { requestCoordintates }
	} = useConfig();
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const autoCompleteTheme = useMemo<EngineTheme>(
		() => ({
			Autocomplete: {
				Autocomplete: {
					autocomplete: theme.autocomplete,
					input: theme.input
				}
			}
		}),
		[]
	);

	if ((isMobile ? requestCoordintates.mobile : requestCoordintates.desktop) && !coordinates) {
		getUserLocation(setCoordinates);
	}

	const { data, loading } = useLocationsQuery({
		variables: {
			parameters: {
				additionalCities: recentSearches.length
					? recentSearches.map((location: Location) => location.iata)
					: [],
				additionalGroups: [
					LocationAdditionalGroupType.Nearest,
					LocationAdditionalGroupType.AdditionalCities,
					LocationAdditionalGroupType.Popular
				],
				query,
				userPosition: coordinates,
				departureFrom: type !== LocationType.Departure && locations.departure ? locations.departure.iata : null
			}
		}
	});

	useEffect(() => {
		const nearestAirport =
			data &&
			data.LocationSuggestions &&
			data.LocationSuggestions.find(location => location.type === LocationGroupType.Nearest);
		if (type === LocationType.Departure && !locations.departure && nearestAirport) {
			props.onSelect(nearestAirport.cities[0]);
		}
	}, [type]);

	useEffect(() => {
		if (
			onNearestAirports &&
			lastSuggestion?.LocationSuggestions?.some(location => location.type === LocationGroupType.Nearest)
		) {
			const locations: Location[] = [];

			lastSuggestion.LocationSuggestions.filter(location => location.type === LocationGroupType.Nearest)?.forEach(
				location => {
					location.cities.forEach(city => locations.push(city));
				}
			);

			onNearestAirports(locations);
		}
	}, [lastSuggestion]);

	const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value);

	useEffect(() => {
		setDelayLoading(true);

		if (query.length >= 0 && data && data.LocationSuggestions && data.LocationSuggestions.length) {
			setLastSuggestion(data);
		} else if (query.length > 0 && (!data || !data.LocationSuggestions || !data.LocationSuggestions.length)) {
			setTimeout(() => {
				setDelayLoading(false);
				setLastSuggestion(null);
			}, 700);
		} else {
			setDelayLoading(false);
		}
	}, [data]);

	const onSelect = (airport: Location): void => {
		setQuery('');

		if (onSelectProp) {
			onSelectProp(airport);
		}
	};

	const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		setQuery('');
		onBlurProp?.(event);
	};

	const options = useMemo(() => {
		const groups = loading || delayLoading ? lastSuggestion : data;

		return parseLocationGroups(groups?.LocationSuggestions.length ? groups?.LocationSuggestions : [], query, type);
	}, [loading, delayLoading, lastSuggestion, data]);

	const dropdownRenderer = (dropdownProps: DropdownRendererParameters<Location>) => (
		<>
			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				<Dropdown
					{...dropdownProps}
					isOpen={dropdownProps.isOpen}
					hasInputData={!!query.trim()}
					onClickOutsideProps={onClickOutsideProps}
					onSelect={onSelect}
					onAdditionalButtonClick={() => setDialogIsOpen(true)}
				/>
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				<MobileDropdown
					{...dropdownProps}
					onAdditionalButtonClick={() => setDialogIsOpen(true)}
					type={type}
					isLoading={loading}
				/>
			</MediaQuery>

			<DirectionsDialog
				isOpen={dialogIsOpen}
				onClose={() => setDialogIsOpen(false)}
				onSelect={location => {
					onSelect(location);
					dropdownProps.onClose();
				}}
				countries={filterGroupsForDirectionsDialog(options)}
			/>
		</>
	);

	return (
		<ThemeProvider value={autoCompleteTheme}>
			<Autocomplete<Location>
				{...autocompleteProps}
				innerRef={ref}
				onClickOutsideProps={onClickOutsideProps}
				isLoading={loading}
				onSelect={onSelect}
				options={options}
				inputValue={query}
				onInputChange={onInputChange}
				optionRenderer={optionRenderer}
				groupRenderer={optionGroupRenderer}
				dropdownRenderer={dropdownRenderer}
				onBlur={onBlur}
			/>
		</ThemeProvider>
	);
});

export default LocationAutocomplete;
